import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Agent } from './api';

const TEMPLATE_LABEL: Record<string, string> = {
  payment: 'Payment',
  trading: 'Trading',
  compliance: 'Compliance',
  orchestrator: 'Orchestrator',
  generic: 'Generic',
};

const TEMPLATE_TONE: Record<string, string> = {
  payment: 'fill-bit2me/10 stroke-bit2me',
  trading: 'fill-amber-50 stroke-amber-500',
  compliance: 'fill-purple-50 stroke-purple-500',
  orchestrator: 'fill-indigo-50 stroke-indigo-500',
  generic: 'fill-surface-soft stroke-line',
};

const NODE_W = 200;
const NODE_H = 92;
const COL_GAP = 60;
const ROW_GAP = 60;

export function AgentsMap({ agents }: { orgId: string; agents: Agent[] }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const nav = useNavigate();

  const { nodes, links, delegateEdges, positionsByID, width, height } = useMemo(() => {
    const grouped = new Map<string, Agent[]>();
    for (const a of agents) {
      const key = a.wallet_id || `solo:${a.id}`;
      const list = grouped.get(key) ?? [];
      list.push(a);
      grouped.set(key, list);
    }
    const ordered: Agent[] = [];
    for (const list of grouped.values()) ordered.push(...list);

    const cols = Math.max(2, Math.min(4, Math.ceil(Math.sqrt(ordered.length))));
    const positions = new Map<string, { x: number; y: number }>();
    ordered.forEach((a, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      positions.set(a.id, {
        x: col * (NODE_W + COL_GAP) + COL_GAP,
        y: row * (NODE_H + ROW_GAP) + ROW_GAP,
      });
    });

    const linkSet: { a: string; b: string; wallet: string }[] = [];
    for (const [walletId, list] of grouped.entries()) {
      if (walletId.startsWith('solo:') || list.length < 2) continue;
      for (let i = 0; i < list.length - 1; i++) {
        linkSet.push({ a: list[i].id, b: list[i + 1].id, wallet: walletId });
      }
    }

    // Orchestrator delegation edges. Each orchestrator points at every
    // agent in its config.delegates; we draw a solid arrow from source
    // to destination so the operator sees who relies on whom at a
    // glance. Self-references and edges to agents not in the canvas
    // are silently dropped.
    const presentIDs = new Set(ordered.map((a) => a.id));
    const edges: { from: string; to: string }[] = [];
    for (const a of ordered) {
      if (a.template_slug !== 'orchestrator') continue;
      const cfgDelegates = (a.config as { delegates?: unknown })?.delegates;
      if (!Array.isArray(cfgDelegates)) continue;
      for (const d of cfgDelegates) {
        if (typeof d !== 'string' || d === a.id || !presentIDs.has(d)) continue;
        edges.push({ from: a.id, to: d });
      }
    }

    const rows = Math.ceil(ordered.length / cols);
    return {
      nodes: ordered,
      links: linkSet,
      delegateEdges: edges,
      positionsByID: positions,
      width: cols * (NODE_W + COL_GAP) + COL_GAP,
      height: Math.max(rows, 1) * (NODE_H + ROW_GAP) + ROW_GAP,
    };
  }, [agents]);

  if (nodes.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line-soft bg-surface-soft/30 p-12 text-center">
        <p className="text-sm text-ink-tertiary">No agents to plot yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line-soft bg-surface-soft/30 p-6 overflow-auto">
      <svg width={width} height={height} className="block">
        <defs>
          <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" className="stroke-line-soft" strokeWidth="0.5" />
          </pattern>
          {/* Arrowhead for delegate edges. orient='auto' aligns it with
              the line direction; refX positions the tip at the line end. */}
          <marker
            id="delegate-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-indigo-500" />
          </marker>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {links.map((l, i) => {
          const pa = positionsByID.get(l.a);
          const pb = positionsByID.get(l.b);
          if (!pa || !pb) return null;
          const x1 = pa.x + NODE_W / 2;
          const y1 = pa.y + NODE_H / 2;
          const x2 = pb.x + NODE_W / 2;
          const y2 = pb.y + NODE_H / 2;
          return (
            <line
              key={`l-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className="stroke-bit2me/40"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
          );
        })}

        {delegateEdges.map(({ from, to }, i) => {
          const pa = positionsByID.get(from);
          const pb = positionsByID.get(to);
          if (!pa || !pb) return null;
          const { x1, y1, x2, y2 } = edgeBetweenNodes(pa, pb);
          return (
            <line
              key={`d-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className="stroke-indigo-500"
              strokeWidth="1.8"
              markerEnd="url(#delegate-arrow)"
            />
          );
        })}

        {nodes.map((a) => {
          const p = positionsByID.get(a.id);
          if (!p) return null;
          const tone = TEMPLATE_TONE[a.template_slug] ?? TEMPLATE_TONE.generic;
          return (
            <g
              key={a.id}
              transform={`translate(${p.x}, ${p.y})`}
              onMouseEnter={() => setHovered(a.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => nav(`/agents/${a.id}`)}
              className="cursor-pointer"
            >
              <rect
                width={NODE_W}
                height={NODE_H}
                rx={10}
                className={`${tone} transition-all`}
                strokeWidth={hovered === a.id ? 2 : 1}
              />
              <text
                x={14}
                y={22}
                className="fill-ink-tertiary font-mono text-[10px] uppercase"
                style={{ letterSpacing: '0.08em' }}
              >
                {TEMPLATE_LABEL[a.template_slug] ?? a.template_slug}
              </text>
              <text x={14} y={44} className="fill-ink-primary text-sm font-semibold">
                {truncate(a.name, 22)}
              </text>
              <text x={14} y={66} className="fill-ink-tertiary font-mono text-[10px]">
                {a.wallet_id ? `wallet ${shortenAddress(a.wallet_id)}` : 'no wallet'}
              </text>
              {a.status !== 'active' && (
                <text x={NODE_W - 14} y={22} textAnchor="end" className="fill-ink-tertiary font-mono text-[10px] uppercase">
                  {a.status}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-tertiary">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-[2px] w-6 bg-indigo-500" />
          orchestrator → delegate
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[2px] w-6"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 50%, transparent 50%)',
              backgroundSize: '6px 2px',
              backgroundRepeat: 'repeat-x',
              color: 'rgb(var(--tw-color-bit2me, 0 102 255) / 0.6)',
            }}
          />
          shared Sigil wallet
        </span>
        <span>Click a node to open the agent.</span>
      </p>
    </div>
  );
}

// edgeBetweenNodes computes the start/end of a line drawn between the
// boundaries of two node rectangles (centred at pa/pb with NODE_W x
// NODE_H). Drawing from boundary to boundary keeps the arrowhead
// visible just outside the destination rect instead of buried inside.
function edgeBetweenNodes(
  pa: { x: number; y: number },
  pb: { x: number; y: number },
): { x1: number; y1: number; x2: number; y2: number } {
  const cax = pa.x + NODE_W / 2;
  const cay = pa.y + NODE_H / 2;
  const cbx = pb.x + NODE_W / 2;
  const cby = pb.y + NODE_H / 2;
  const dx = cbx - cax;
  const dy = cby - cay;
  if (dx === 0 && dy === 0) {
    return { x1: cax, y1: cay, x2: cbx, y2: cby };
  }
  const tx = Math.abs(dx) > 0 ? (NODE_W / 2) / Math.abs(dx) : Infinity;
  const ty = Math.abs(dy) > 0 ? (NODE_H / 2) / Math.abs(dy) : Infinity;
  const t = Math.min(tx, ty);
  return {
    x1: cax + dx * t,
    y1: cay + dy * t,
    x2: cbx - dx * t,
    y2: cby - dy * t,
  };
}

function shortenAddress(a: string): string {
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
